import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Grid, Stack } from '@mui/material';
import { isEmpty, omit } from 'lodash-es';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FaCalendarAlt, FaPersonBooth } from 'react-icons/fa';
import { IoMdBriefcase } from 'react-icons/io';
import {
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsGlobalFilters,
  IComposableDashboardContext,
  IComposableDashboardFiltersContext,
  IStageStatusPair,
} from 'wisestep-api';
import {
  WsColors,
  WsFilterDialog,
  WsButton,
  WsFilterTags,
  WsDatePicker,
  formattedDate,
  WsButtonGroup,
  WsSelect,
} from 'wisestep-base';
import { IFilterList, WsSelectObj } from 'wisestep-base/src';

import {
  ClientNamesFilter2,
  JobStatusFilter,
  JobTypeFilter,
} from '../../../../components/filters/components';
import AccountManagerUserFilter from '../../../../components/filters/components/AccountManagerFilter';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import WsTeamFilter from '../../../../components/filters/components/WsTeamsFilter';
import {
  onSmartDateSelect,
  smartDateOptionsArrayForComposable,
} from '../../../../features/analytics/utils/smartDateSelectHelper';
import { IBusinessData } from '../../../../features/crmSettings/customizeStageStatus/helper';
import { useStageStatusContext } from '../../../../features/submissions/context/StageStatusContext';
import StageStatusFilterPopUp from '../../../../features/submissions/submissionListing/components/stageStatusFilterPopup';
import { extractEmail } from '../../../../utils/StringUtils';
import convertBdToStageStatusPair from '../../helper/convertBdToStageStatusPair';
import { getTeamIds, getTeamNames } from '../../helper/handleTeamsIds';
import { getCandidateJobStatus } from '../../helper/postgreDrawerChartsList';
import { optionsForJobTypeFilter } from '../../helper/timePeriodList';
import useBusinessUnits from '../../hooks/useBusinessDefinition';
import { useComposableDashboardsContext } from '../../hooks/useComposableDashboardsContext';
import { useComposableDashboardsFiltersContext } from '../../hooks/useComposableDashboardsFiltersContext';
import { useComposableDynamicListingContext } from '../../hooks/useComposableDynamicListingContext';

export const statusOptions: WsSelectObj[] = [
  {
    label: 'Active',
    value: 'Active',
  },
  {
    label: 'Close',
    value: 'Close',
  },
  {
    label: 'Both',
    value: 'Both',
  },
];

const dateFilterOptions: WsSelectObj[] = [
  {
    label: 'Created On',
    value: 'CREATED_BETWEEN',
  },
  {
    label: 'Created Or Modified On',
    value: 'CREATED_OR_UPDATED_BETWEEN',
  },
];

interface IDashboardFilters {
  disableFiltersList?: ComposableDashboardFiltersEnum[];
  userEmail?: string;
  showFilterTags?: boolean;
}

const DashboardFilters = ({
  disableFiltersList,
  userEmail,
  showFilterTags = true,
}: IDashboardFilters) => {
  const {
    filters,
    setFilters,
    filtersAdddedToDashboard,
    refreshFiltersState,
    dependentFilters,
    overriddenFilters,
  } = useComposableDashboardsFiltersContext() as Required<IComposableDashboardFiltersContext>;

  const { allBusinessDefinition } = useComposableDynamicListingContext();
  const isControlWithSmartFilter = useRef<boolean>(false);
  const formMethods = useForm<IComposableAnalyticsGlobalFilters>({ defaultValues: filters });
  const { setLoader } = useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const { handleSubmit, setValue, reset, getValues, control } = formMethods;
  const stageStatus = useStageStatusContext();
  const businessUnits = useBusinessUnits();

  useEffect(() => {
    if (setFilters && userEmail)
      setFilters((prev) => ({ ...prev, recruiterNames: [userEmail], recruiterId: userEmail }));
  }, [setFilters, userEmail]);

  const onSubmit = useCallback(
    (data: IComposableAnalyticsGlobalFilters) => {
      const tempData = { ...data };
      if (!isEmpty(data.status)) {
        tempData.candidateStageStatusPipelineData = stageStatus?.stageStatusPipeline ?? '';
      }
      if (!isEmpty(data.accountManagers)) {
        tempData.accountManagers = data.accountManagers?.map((email) => extractEmail(email)) ?? [];
      }
      if (!isEmpty(data.teamIds)) {
        tempData.teamNames = getTeamNames(data);
        tempData.teamIds = getTeamIds(data);
      }
      if (!isEmpty(data.businessUnitId)) tempData.businessUnitId = data.businessUnitId as string;
      if (!isEmpty(data.bdId)) {
        tempData.currentStageCurrentStatusPairs = convertBdToStageStatusPair(
          allBusinessDefinition?.find((bd) => bd.id === data.bdId) as IBusinessData
        );
        tempData.bdId = data.bdId;
        tempData.jobStatus = getCandidateJobStatus(
          allBusinessDefinition?.find((bd) => bd.id === data.bdId) as IBusinessData
        );
      }
      if (data?.fromDate) {
        tempData.fromDate = new Date(data.fromDate).toISOString();
      }
      if (data?.toDate) {
        tempData.toDate = new Date(data.toDate).toISOString();
      }
      setFilters?.(tempData);
    },
    [allBusinessDefinition, setFilters, stageStatus?.stageStatusPipeline]
  );

  const [openStageStatusFilter, setOpenStageStatusFilter] = useState<boolean>(false);
  const onSelectStageStatus = useCallback(
    (data: IStageStatusPair[]) => {
      setValue('currentStageCurrentStatusPairs', data);
      setOpenStageStatusFilter(false);
    },
    [setValue]
  );
  const formState = useWatch({ control }) as IComposableAnalyticsGlobalFilters;

  const filterTagsMap = useCallback(
    (overrideFilters?: IComposableAnalyticsGlobalFilters) => {
      let filterState;
      if (overrideFilters) filterState = overrideFilters;
      else filterState = formState;
      return {
        [ComposableDashboardFiltersEnum.RECRUITER]: !isEmpty(filterState?.recruiterNames)
          ? { id: 'recruiterNames', tag: `Recruiters: ${filterState?.recruiterNames?.join(', ')}` }
          : undefined,
        [ComposableDashboardFiltersEnum.CLIENT]: !isEmpty(filterState?.clientNames)
          ? { id: 'clientNames', tag: `Clients: ${filterState?.clientNames?.join(', ')}` }
          : undefined,
        [ComposableDashboardFiltersEnum.ACCOUNT_MANAGER]: !isEmpty(filterState?.accountManagers)
          ? {
              id: 'accountManagers',
              tag: `Account Managers: ${filterState?.accountManagers?.join(', ')}`,
            }
          : undefined,
        [ComposableDashboardFiltersEnum.STATUS]: !isEmpty(filterState?.status)
          ? { id: 'status', tag: `Candidate Status: ${filterState?.status}` }
          : undefined,
        [ComposableDashboardFiltersEnum.TEAM]: !isEmpty(filterState?.teamIds)
          ? {
              id: 'teamIds',
              tag: `Teams: ${getTeamNames(filterState).join(', ')}`,
            }
          : undefined,
        [ComposableDashboardFiltersEnum.STAGE_STATUS]: !isEmpty(filterState?.stageStatus)
          ? {
              id: 'stageStatus',
              tag: (filterState?.stageStatus ?? [])
                .map((item) => `${item.applicationStage}:${item.applicationState}`)
                .join(' ,'),
            }
          : undefined,
        [ComposableDashboardFiltersEnum.FILTER_DATE_BY]: !isEmpty(filterState?.datesUnionBy)
          ? { id: 'datesUnionBy', tag: `Filter By: ${filterState?.datesUnionBy}` }
          : undefined,
        [ComposableDashboardFiltersEnum.DATE_RANGE]: (() => {
          const fromExp = filterState?.fromDate
            ? `From: ${formattedDate(filterState.fromDate)}`
            : '';
          const toExp = filterState?.toDate ? `To: ${formattedDate(filterState.toDate)}` : '';
          return fromExp || toExp
            ? {
                id: 'fromDate&toDate',
                tag: `${fromExp}${fromExp && toExp && ' -- '}${toExp}`,
              }
            : undefined;
        })(),
        [ComposableDashboardFiltersEnum.JOB_STATUS]: !isEmpty(filterState?.jobStatus)
          ? { id: 'jobStatus', tag: `Job Status: ${filterState?.jobStatus}` }
          : undefined,
        [ComposableDashboardFiltersEnum.BUSINESS_DEFINITION]: !isEmpty(filterState?.bdId)
          ? {
              id: 'bdId',
              tag: `Business Defintiion: ${
                allBusinessDefinition?.find((bd) => bd.id === filterState?.bdId)
                  ?.businessDefinitionName
              }`,
            }
          : undefined,
        [ComposableDashboardFiltersEnum.BUSINESS_UNIT]: !isEmpty(filterState?.businessUnitId)
          ? {
              id: 'businessUnitId',
              tag: `Business Unit: ${
                businessUnits?.find((bu) => bu.id === filterState?.businessUnitId)?.name
              }`,
            }
          : undefined,
        [ComposableDashboardFiltersEnum.JOB_TYPE]: !isEmpty(filterState?.jobType)
          ? { id: 'jobType', tag: `Job Type: ${filterState?.jobType}` }
          : undefined,
      };
    },
    [allBusinessDefinition, businessUnits, formState]
  );

  const addFilterTags = useCallback(
    (initalise?: boolean, overrideFilters?: IComposableAnalyticsGlobalFilters) => {
      const tagsMap = filterTagsMap(overrideFilters);
      const filtersList: IFilterList[] =
        filtersAdddedToDashboard?.filter((f) => tagsMap[f]).map((f) => tagsMap[f] as IFilterList) ??
        [];
      if (!initalise) setFilterTagsList(filtersList);
      else return filtersList;
    },
    [filterTagsMap, filtersAdddedToDashboard]
  );

  const filterComponentsMap: Record<ComposableDashboardFiltersEnum, JSX.Element> = useMemo(
    () => ({
      [ComposableDashboardFiltersEnum.RECRUITER]: (
        <WsFilterDialog
          name="Recruiter Names"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Recruiter"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <RecruiterSuggestions
              name="recruiterNames"
              multiple={!disableFiltersList?.includes(ComposableDashboardFiltersEnum.RECRUITER)}
              defaultValue={formState?.recruiterNames}
              disabled={disableFiltersList?.includes(ComposableDashboardFiltersEnum.RECRUITER)}
            />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.CLIENT]: (
        <WsFilterDialog
          name="Client Name"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Client"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <ClientNamesFilter2 multiple name="clientNames" />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.ACCOUNT_MANAGER]: (
        <WsFilterDialog
          name="Account Manager"
          icon={<ManageAccountsIcon fontSize="medium" color="primary" />}
          label="Account"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <AccountManagerUserFilter name="accountManagers" multiple={true} isBorderStyle={true} />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.STATUS]: (
        <WsFilterDialog
          name="Candidate Status"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Candidate Status"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <WsSelect
              name="status"
              selectArray={statusOptions}
              placeholder="Select candidate status"
              variant="outlined"
              size="small"
            />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.TEAM]: (
        <WsFilterDialog
          name="Team Names"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Teams"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <WsTeamFilter name="teamIds" multiple={true} defaultValue={getTeamNames(formState)} />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.STAGE_STATUS]: (
        <WsFilterDialog
          name="Stage Status"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Application Stage and Status"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <>
              <WsButton variant="text" onClick={() => setOpenStageStatusFilter(true)}>
                Stage Status
              </WsButton>
              <StageStatusFilterPopUp
                open={openStageStatusFilter}
                onClose={() => setOpenStageStatusFilter(false)}
                onSelect={onSelectStageStatus}
                stageStatus={getValues('currentStageCurrentStatusPairs')}
              />
            </>
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.FILTER_DATE_BY]: (
        <WsFilterDialog
          name="Filter Date By"
          icon={<FaPersonBooth size={21} color={WsColors.TheBlues500} />}
          label="Filter Date By"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <WsSelect
              name="datesUnionBy"
              selectArray={
                dateFilterOptions?.map((option) => ({
                  label: option.label ?? '',
                  value:
                    (option.value as
                      | 'CREATED_BETWEEN'
                      | 'CREATED_OR_UPDATED_BETWEEN'
                      | 'UPDATED_BETWEEN') ?? '',
                })) ?? []
              }
              placeholder="Select filter date by"
              variant="outlined"
              size="small"
            />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.DATE_RANGE]: (
        <Stack minWidth={300} rowGap={0.5}>
          <WsFilterDialog
            name="Date Range"
            icon={<FaCalendarAlt size={21} color={WsColors.TheBlues500} />}
            label="Date Range"
            onClick={(args) => {
              if (args == 'onFilterApply') {
                handleSubmit((data) => {
                  if (data.fromDate) {
                    data.fromDate = new Date(data.fromDate);
                    data.fromDate.setHours(0, 0, 0, 0);
                  }
                  if (data.toDate) {
                    data.toDate = new Date(data.toDate);
                    data.toDate.setHours(23, 59, 59, 999);
                  }
                  onSubmit(data);
                  addFilterTags(false, {
                    ...formState,
                    fromDate: getValues('fromDate'),
                    toDate: getValues('toDate'),
                  } as IComposableAnalyticsGlobalFilters);
                })();
              }
            }}
          >
            <Stack
              spacing={1}
              p={2}
              width={450}
              onBlur={() => {
                isControlWithSmartFilter.current = false;
              }}
            >
              <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
              {!dependentFilters?.['singleDateField'] && (
                <WsDatePicker name="toDate" label="To Date" disableFuture={true} />
              )}
            </Stack>
          </WsFilterDialog>
          <WsButtonGroup
            inputTitle={''}
            buttonsList={
              dependentFilters.smartFilters.length > 0
                ? dependentFilters.smartFilters
                : smartDateOptionsArrayForComposable
            }
            size="small"
            selectedIndexValue={(_, value) => {
              isControlWithSmartFilter.current = true;
              onSmartDateSelect(setValue)[value]();
              handleSubmit((data) => {
                onSubmit(data);
                addFilterTags(false, {
                  ...formState,
                  fromDate: getValues('fromDate'),
                  toDate: getValues('toDate'),
                } as IComposableAnalyticsGlobalFilters);
              })();
            }}
            triggerReset={!isControlWithSmartFilter.current}
          />
        </Stack>
      ),
      [ComposableDashboardFiltersEnum.JOB_STATUS]: (
        <WsFilterDialog
          name="Job Status"
          icon={<IoMdBriefcase size={21} color={WsColors.TheBlues500} />}
          label="Job Status"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <JobStatusFilter name="jobStatus" />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.BUSINESS_UNIT]: (
        <WsFilterDialog
          name="Business Unit"
          icon={<IoMdBriefcase size={21} color={WsColors.TheBlues500} />}
          label="Business Unit"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <WsSelect
              name="businessUnitId"
              selectArray={
                businessUnits.map((bu) => ({ label: bu?.name, value: bu?.id })) as WsSelectObj[]
              }
              placeholder="Select business Unit"
              variant="outlined"
              size="small"
            />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.BUSINESS_DEFINITION]: (
        <WsFilterDialog
          name="Business Definition"
          icon={<IoMdBriefcase size={21} color={WsColors.TheBlues500} />}
          label="Business Definition"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <WsSelect
              name="bdId"
              selectArray={
                allBusinessDefinition?.map((bd) => ({
                  label: bd.businessDefinitionName ?? '',
                  value: bd.id ?? '',
                })) ?? []
              }
              placeholder="Select business Definition"
              variant="outlined"
              size="small"
            />
          </Grid>
        </WsFilterDialog>
      ),
      [ComposableDashboardFiltersEnum.JOB_TYPE]: (
        <WsFilterDialog
          name="Job Type"
          icon={<IoMdBriefcase size={21} color={WsColors.TheBlues500} />}
          label="Job Type"
        >
          <Grid pl={2} py={1.5} minWidth={480}>
            <JobTypeFilter name="jobType" optionsList={optionsForJobTypeFilter} />
          </Grid>
        </WsFilterDialog>
      ),
    }),
    [
      disableFiltersList,
      formState,
      openStageStatusFilter,
      onSelectStageStatus,
      getValues,
      dependentFilters,
      businessUnits,
      allBusinessDefinition,
      setValue,
      handleSubmit,
      onSubmit,
      addFilterTags,
    ]
  );

  const [filterTagsList, setFilterTagsList] = useState<IFilterList[]>(
    () => addFilterTags(true) ?? []
  );

  const resetFormToState = (state?: Partial<IComposableAnalyticsGlobalFilters>) => {
    if (state) {
      reset(state);
    } else {
      reset(overriddenFilters.current);
      setFilterTagsList([]);
    }
    handleSubmit(onSubmit)();
    isControlWithSmartFilter.current = false;
  };

  useEffect(() => {
    if (refreshFiltersState?.current) {
      reset(filters);
      addFilterTags(false, filters);
      refreshFiltersState.current = false;
      isControlWithSmartFilter.current = false;
      setLoader(false);
    }
  }, [addFilterTags, filters, refreshFiltersState, reset, setLoader]);

  //Set toDate equal to fromDate in case of single date field
  useEffect(() => {
    if (dependentFilters?.singleDateField && !isEmpty(formState?.fromDate?.toString()))
      setValue('toDate', formState?.fromDate);
  }, [dependentFilters?.singleDateField, formState?.fromDate, getValues, setValue]);

  return (
    <FormProvider {...formMethods}>
      {filtersAdddedToDashboard?.length > 0 ? (
        <>
          <Grid
            container
            width="100%"
            bgcolor={WsColors.White}
            p={2}
            borderRadius={2}
            justifyContent="space-between"
            component="form"
            onSubmit={handleSubmit((data) => {
              onSubmit(data);
              addFilterTags();
            })}
            onReset={() => resetFormToState()}
          >
            <Grid item container xs={10.5} justifyContent="flex-start" rowGap={1}>
              {filtersAdddedToDashboard &&
                filtersAdddedToDashboard.map((filterEnum) => (
                  <Grid item key={filterEnum}>
                    {filterComponentsMap[filterEnum]}
                  </Grid>
                ))}
            </Grid>
            <Grid item container xs={1.5} justifyContent="space-between">
              <WsButton type="submit">Apply</WsButton>
              <WsButton type="reset">Clear</WsButton>
            </Grid>
          </Grid>
          {showFilterTags && (
            <WsFilterTags
              filtersList={filterTagsList}
              onDelete={(chip) => {
                if (!userEmail && !chip?.includes('recruiterNames')) {
                  const tagToDelete = filterTagsList.find((f) => f.tag == chip);
                  const currentFormState = getValues();
                  setFilterTagsList((prev) => prev.filter((f) => f.id !== tagToDelete?.id));
                  resetFormToState(omit(currentFormState, tagToDelete?.id?.split('&') ?? ''));
                }
              }}
            />
          )}
        </>
      ) : (
        <></>
      )}
    </FormProvider>
  );
};

export default DashboardFilters;
